import { ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import Competence, { CompetenceItem } from "../../../models/Competence";
import { useOrganisation } from "../../../networking/organisation.hooks";

export interface ICompetencePillsProps {
	value?: Competence;
	onChange?: (val: any) => any;
	hideSkills?: boolean;
	errors?: any;
	hideLabel?: boolean;
	resetCompetences?: boolean;
}

const COLUMN_CLS = "h-52 overflow-y-auto rounded-md border border-solid border-gray-300 bg-white";

const CompetenceOption: React.FC<{
	label?: string;
	selected: boolean;
	hasChildren?: boolean;
	onClick: () => void;
}> = ({ label, selected, hasChildren, onClick }) => (
	<button
		type="button"
		onClick={onClick}
		className={cn(
			"flex w-full cursor-pointer select-none items-center justify-between gap-2 border-0 px-3 py-2 text-left text-sm text-gray-800 transition-colors",
			selected ? "bg-blue-50 text-blue-900 hover:bg-blue-50" : "bg-transparent hover:bg-gray-50"
		)}
	>
		<span className="truncate">{label}</span>
		{hasChildren && <ChevronRight size={14} className="shrink-0 text-gray-400" />}
	</button>
);

const Competences: React.FC<ICompetencePillsProps> = ({
	value,
	onChange,
	hideSkills = false,
	errors,
	hideLabel,
	resetCompetences,
}) => {
	const [values, setValues] = useState(value);
	const { data: organisation } = useOrganisation();

	const [industry, setIndustry] = useState<CompetenceItem>();
	const [skills, setSkills] = useState<string[]>([]);
	const [service, setService] = useState<CompetenceItem>();

	const { t } = useTranslation();

	useEffect(() => {
		if (value && organisation) {
			const branch = organisation?.competences?.find((el) => el.key === value.branch);
			if (branch) setIndustry(branch);

			const service = (branch?.items || []).find((el) => el.key === value.service);

			if (service) {
				setService(service);
			}
			if (value.skills) setSkills(value.skills);
		}
	}, [value, organisation]);

	useEffect(() => {
		if (onChange) {
			onChange(values);
		}
	}, [values, onChange]);

	useEffect(() => {
		if (resetCompetences) {
			setIndustry(undefined);
			setSkills([]);
			setService(undefined);
		}
	}, [resetCompetences]);

	useEffect(() => {
		const updateCompetence: Competence = {
			branch: industry?.key as string,
			service: service?.key as string,
			skills: skills,
		};

		if (
			values?.branch !== updateCompetence.branch ||
			values?.service !== updateCompetence.service ||
			values?.skills?.join(",") !== updateCompetence.skills?.join(",")
		) {
			setValues(updateCompetence);
		}
	}, [skills, industry, service]);

	const handleIndustryClick = (item: CompetenceItem) => {
		if (item) {
			setIndustry(item);
			setService(undefined);
			setSkills([]);
		}
	};

	const handleServiceClick = (item: CompetenceItem) => {
		if (item) {
			setService(item);
			setSkills([]);
		}
	};

	const handelSkillItemClick = (item: CompetenceItem) => {
		if (item) {
			setSkills((prev) => {
				if (prev.includes(item.key)) {
					return prev.filter((el) => el !== item.key);
				} else {
					return [...prev, item.key];
				}
			});
		}
	};

	return (
		<div>
			{!hideLabel && <p className="mb-4 text-sm font-bold text-gray-600">{t("COMPETENCE")}</p>}

			<div className={cn("grid gap-3", hideSkills ? "grid-cols-2" : "grid-cols-3")}>
				{/* Archived competences are never offered when picking — but stay
				    selectable via `value` so existing selections keep resolving. */}
				<div className={COLUMN_CLS}>
					{organisation?.competences
						?.filter((item) => !item.archived)
						.map((item) => (
							<CompetenceOption
								key={item?.key}
								label={item?.name.sv}
								selected={item.key === industry?.key}
								hasChildren={!!(item.items || []).length}
								onClick={() => handleIndustryClick(item)}
							/>
						))}
				</div>

				<div className={COLUMN_CLS}>
					{(industry?.items || [])
						.filter((item) => !item.archived)
						.map((item) => (
							<CompetenceOption
								key={industry?.key + "_" + item?.key}
								label={item?.name.sv}
								selected={item.key === service?.key}
								hasChildren={!!(item.items || []).length}
								onClick={() => handleServiceClick(item)}
							/>
						))}
				</div>

				{!hideSkills && (
					<div className={cn(COLUMN_CLS, !service?.items?.length && "opacity-50")}>
						{(service?.items || [])
							.filter((item) => !item.archived)
							.sort((a, b) => a.name.sv!.localeCompare(b.name.sv!))
							.map((item) => (
								<CompetenceOption
									key={service?.key + "_" + item?.key}
									label={item?.name.sv}
									selected={skills.includes(item.key)}
									onClick={() => handelSkillItemClick(item)}
								/>
							))}
					</div>
				)}
			</div>

			{errors?.branch?.message && (
				<p className="mt-2 text-xs text-red-600">{t(errors.branch.message as any)}</p>
			)}
			{errors?.service?.message && (
				<p className="mt-2 text-xs text-red-600">{t(errors.service.message as any)}</p>
			)}
		</div>
	);
};

export default Competences;
